import type { ReactNode } from 'react'
import { Button, Tooltip } from '@highlight/ui'
import { FileText, LockKeyhole } from 'lucide-react'
import { usePlanAllowances, type AllowanceCount } from '@renderer/chat/hooks/use-plan-allowances'
import { briefsLeftCopy, briefsOutCopy, resetDateLabel } from '@renderer/components/pricing/limits-copy'
import { LockedPage, LockNotice, PlanUpgradeButton } from '@renderer/components/pricing/locked-page'
import { MEETING_HOURS_EXHAUSTED_TOOLTIP } from '../../../components/meeting/brief/use-meeting-hours-exhausted'
import { trackGeneration, upgradeForBriefs, useBriefsUpgradePlan, useMeetingBriefGeneration } from './brief-generation'
import type { MeetingViewModel } from '../use-meeting-view-model'

// A brief that reads as worth having, so the haze says "this is what you'd get" rather than "something broke".
// Specific enough to tempt, never legible through the blur.
const SAMPLE_BRIEF = {
  lead: 'You’re meeting to agree what ships next. Two questions are still open, and there’s an update on the last release.',
  points: [
    'Onboarding drop-off fell after the calendar step moved earlier.',
    'One open question is whether the panel opens on the follow-up or the feed.',
    'Three accounts are still waiting on a reply about pricing.',
  ],
  ask: 'Ask which open question blocks the release.',
}

interface LockedBriefProps {
  model: MeetingViewModel
  /** Free's briefs this period. */
  briefs: AllowanceCount
  /** Sits above the blurred brief, untouched by it: the Join button before a meeting. */
  top?: ReactNode
}

/**
 * A meeting's brief on Free: blurred until the user spends one of the month's
 * briefs on it, so they choose which meetings get one. Generate prepares the
 * brief; with none left, the only way on is upgrading.
 */
export function LockedBrief({ model, briefs, top }: LockedBriefProps) {
  const plan = useBriefsUpgradePlan()
  const { outOfHours, isGenerating, create } = useMeetingBriefGeneration(model)
  const resets = resetDateLabel(usePlanAllowances().data?.periodEndsAt)
  const left = briefsLeftCopy(briefs.remaining, briefs.limit, resets)
  const out = briefsOutCopy({ plan: 'free', upgradeTo: plan, resets })

  const unblur = () => {
    create()
    trackGeneration('generate', model.briefCalendarEventId)
  }

  // Spending the last brief refetches the count to 0 mid-generation; keep the Generating state until the brief lands.
  const notice =
    briefs.remaining > 0 || isGenerating ? (
      <LockNotice
        icon={<FileText className="size-5" />}
        title={left.title}
        description={left.body}
        actions={
          // Wrapped so the reason still shows on hover: a disabled button fires no pointer events.
          <Tooltip disabled={!outOfHours}>
            <Tooltip.Trigger render={<div className="flex" />}>
              <Button
                variant="primary"
                size="md"
                disabled={isGenerating || outOfHours}
                aria-busy={isGenerating}
                onClick={unblur}
              >
                {isGenerating ? 'Generating…' : 'Generate'}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{MEETING_HOURS_EXHAUSTED_TOOLTIP}</Tooltip.Content>
          </Tooltip>
        }
      />
    ) : (
      <LockNotice
        icon={<LockKeyhole className="size-5" />}
        title={out.title}
        description={out.body}
        actions={
          <PlanUpgradeButton plan={plan} onClick={() => upgradeForBriefs(plan, 'meeting view')}>
            {out.cta}
          </PlanUpgradeButton>
        }
      />
    )

  return (
    <div className="flex min-h-[440px] flex-1 flex-col">
      {top && <div className="flex justify-center px-5 pb-2 pt-4">{top}</div>}
      <LockedPage notice={notice}>
        <div className="type-prose-sm text-foreground flex flex-col gap-3 px-5 pt-2">
          <p className="m-0">{SAMPLE_BRIEF.lead}</p>
          <h4 className="type-ui-sm m-0 font-semibold">Where things stand</h4>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
            {SAMPLE_BRIEF.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <h4 className="type-ui-sm m-0 font-semibold">Worth asking</h4>
          <p className="m-0">{SAMPLE_BRIEF.ask}</p>
        </div>
      </LockedPage>
    </div>
  )
}
